import React, { useEffect, useRef, useState } from 'react';
import { biGet, today, errText } from './bi/biApi';
import { Shell, Card, Kpi, Tabs, Empty, Loading, ErrorBox } from './bi/BiUi';
import { COLORS, AttendancePie, MultiLine, VBars } from './bi/DashCharts';
import { RiskDots } from './bi/DashExtras';
import Student360Modal from './bi/Student360Modal';
import { exportNodeToPdf } from './bi/exportPdf';

/** Class Teacher dashboard: KPIs + attendance pie, weekly line, subject homework bars, risk scatter + roster. */
export default function ClassAnalyticsDashboard({ user }) {
  const classId = user?.class || user?.assignedClass || '';
  const [range, setRange] = useState('weekly');
  const [daily, setDaily] = useState(null);
  const [trend, setTrend] = useState(null);
  const [month, setMonth] = useState(null);
  const [risk, setRisk] = useState([]);
  const [year, setYear] = useState('');
  const [pick, setPick] = useState(null);
  const [err, setErr] = useState('');
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);
  const date = today();

  useEffect(() => {
    if (!classId) return;
    setErr('');
    biGet(user, '/api/analytics/daily', { classId, date }).then(setDaily).catch((e) => setErr(errText(e)));
    biGet(user, '/api/analytics/risk-matrix', { classId, date }).then((r) => setRisk(r.students)).catch((e) => setErr(errText(e)));
    biGet(user, '/api/analytics/yearly', { classId, date }).then((y) => setYear(y.academicYear)).catch(() => {});
    biGet(user, '/api/analytics/monthly', { classId, date }).then(setMonth).catch(() => {});
  }, [user, classId, date]);

  useEffect(() => {
    if (!classId) return;
    setTrend(null);
    biGet(user, `/api/analytics/${range}`, { classId, date }).then(setTrend).catch((e) => setErr(errText(e)));
  }, [user, classId, range, date]);

  const exportPdf = async () => {
    setExporting(true);
    try {
      await exportNodeToPdf(printRef.current, {
        title: `Class ${classId} - ${range === 'monthly' ? 'Monthly' : 'Weekly'} Summary`,
        subtitle: `Generated ${date}`, filename: `class-${classId}-${range}-${date}.pdf`
      });
    } catch (e) { setErr(`PDF export failed: ${e.message}`); } finally { setExporting(false); }
  };

  if (!classId) return <Shell><Card><Empty>No class is assigned to your account.</Empty></Card></Shell>;
  const series = trend ? (trend.days || trend.trend || []) : [];
  const critical = risk.filter((r) => r.level === 'critical');
  const subjects = month ? (month.subjectConsistency || []).map((s) => ({ label: s.name || s.subject, completion: s.consistencyPct })) : null;
  const hasToday = !!(daily && daily.total > 0);

  return (
    <Shell>
      <div className="flex flex-wrap items-center gap-4 border-b border-slate-800 pb-5">
        <div className="mr-auto">
          <h2 className="text-2xl font-black tracking-tight text-white">Class {classId} Analytics</h2>
          <p className="text-sm font-medium text-slate-400">
            School Intelligence View • <span className="text-sky-400">{date}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={range} onChange={setRange} options={[['weekly', '7 Days'], ['monthly', '30 Days']]} />
          <button type="button" onClick={exportPdf} disabled={exporting || !trend}
            className="rounded-xl bg-gradient-to-r from-sky-400 to-indigo-500 px-4 py-2 text-sm font-bold text-slate-950 shadow-lg shadow-sky-500/20 transition-all hover:scale-105 active:scale-95 disabled:opacity-50">
            {exporting ? 'Preparing...' : '⬇ Export PDF'}
          </button>
        </div>
      </div>

      <ErrorBox message={err} />

      <div ref={printRef} className="space-y-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi label="Attendance Today" value={daily ? (hasToday ? `${daily.attendancePct}%` : 'Pending') : '...'} tone="emerald" icon="📋" hint={daily ? `${daily.present}/${daily.total} present` : ''} />
          <Kpi label="Absent Students" value={daily ? daily.absent : '...'} tone="rose" icon="🔴" hint="Needs follow-up" />
          <Kpi label="Approved Leave" value={daily ? daily.leaveCount : '...'} tone="amber" icon="🟡" hint="Documented" />
          <Kpi label="Critical ( <75% )" value={critical.length} tone="indigo" icon="⚠️" hint="At academic risk" />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card title="Attendance Distribution" subtitle="Today's presence vs absence breakdown">
            {!daily ? <Loading /> : hasToday
              ? <AttendancePie present={daily.present} absent={daily.absent} leave={daily.leaveCount} centerLabel={`${daily.attendancePct}%`} />
              : <Empty>Attendance has not been marked today.</Empty>}
          </Card>

          <Card title="Engagement Trends" subtitle={`${range === 'monthly' ? '30 days' : '7 days'} historical data`}>
            {!trend ? <Loading /> : series.length
              ? <MultiLine data={series} refLine={75} series={[
                { key: 'attendancePct', name: 'Attendance', color: COLORS.present },
                { key: 'homeworkPct', name: 'Diary Submission', color: COLORS.academic }]} />
              : <Empty />}
          </Card>

          <Card title="Subject Performance" subtitle="Diary completion consistency (30 days)">
            {!subjects ? <Loading /> : subjects.length
              ? <VBars data={subjects} bars={[{ key: 'completion', name: 'Homework discipline', color: COLORS.academic }]} />
              : <Empty>No subject diary entries yet.</Empty>}
          </Card>

          <Card title="Risk Assessment Map" subtitle="Attendance (X) vs Homework (Y). Red dots = Critical Risk.">
            {risk.length ? <RiskDots students={risk} onSelect={(p) => setPick(p.studentId)} /> : <Empty>No student profiles yet.</Empty>}
          </Card>
        </div>

        <Card title="Critical Follow-up List" subtitle="Students below the 75% attendance threshold">
          {critical.length === 0 ? <Empty>Excellent! All students are above the 75% threshold.</Empty> : (
            <ul className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {critical.map((r) => (
                <li key={r.studentId}>
                  <button type="button" onClick={() => setPick(r.studentId)}
                    className="flex w-full items-center justify-between rounded-2xl border border-rose-500/30 bg-rose-500/5 px-4 py-3 text-left transition-all hover:border-rose-500/60 hover:bg-rose-500/10">
                    <span className="font-bold text-white truncate pr-2">{r.name}</span>
                    <span className="rounded-full bg-rose-500/20 px-3 py-1 text-xs font-black text-rose-400">{r.attendancePct}%</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      {pick && <Student360Modal user={user} classId={classId} studentId={pick} year={year} onClose={() => setPick(null)} />}
    </Shell>
  );
}

